// src/pages/admin/AdminHubImages.js
// Photos de la page d'accueil maxula.ma — Maxula
// AbodyStudio Limited

import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { FiExternalLink, FiImage, FiRefreshCw, FiTrash2, FiUpload } from 'react-icons/fi';
import API from '../../services/api';

const HUB_URL = 'https://maxula.ma';
const MAX_MO = 5;

const AdminHubImages = () => {
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await API.get('/hub-images');
      const data = res?.data?.data || {};
      setSlots(Object.keys(data).map((key) => ({ key, ...data[key] })));
    } catch (e) {
      toast.error("Impossible de charger les photos");
      setSlots([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const send = async (key, file, remove = false) => {
    if (file && file.size > MAX_MO * 1024 * 1024) {
      return toast.error(`Photo trop lourde (${MAX_MO} Mo maximum)`);
    }
    try {
      setBusy(key);
      const fd = new FormData();
      fd.append('slot', key);
      if (remove) fd.append('remove', 'true');
      else fd.append('image', file);

      const res = await API.put('/admin/hub-images', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      // On lit l'URL RENVOYÉE par le serveur plutôt que de reconstruire la
      // nôtre : le nom du fichier est décidé côté serveur (horodatage), le
      // deviner ici donnerait une image cassée au premier renommage.
      const url = res?.data?.data?.url || null;
      setSlots((prev) => prev.map((s) => (s.key === key ? { ...s, url } : s)));
      toast.success(res?.data?.message || 'Enregistré');
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Envoi impossible');
    } finally {
      setBusy(null);
    }
  };

  const pick = (key) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = () => {
      if (input.files && input.files[0]) send(key, input.files[0]);
    };
    input.click();
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex flex-wrap justify-between items-center gap-3 mb-2">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Photos de maxula.ma</h1>
          <p className="text-gray-600">
            Les visuels de la page d'accueil du groupe. Le changement est visible
            en ligne immédiatement.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <a
            href={HUB_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 px-4 py-2 border rounded-lg hover:bg-gray-50"
          >
            <FiExternalLink /> Voir la page
          </a>
          <button onClick={load} className="flex items-center gap-2 px-4 py-2 border rounded-lg hover:bg-gray-50">
            <FiRefreshCw /> Rafraîchir
          </button>
        </div>
      </div>

      <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900 mb-6">
        Une photo retirée n'est pas remplacée par du vide : la page reprend le
        visuel d'origine livré dans le fichier.
      </div>

      {loading ? (
        <div className="text-gray-500">Chargement...</div>
      ) : (
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {slots.map((s) => (
            <div key={s.key} className="bg-white rounded-xl shadow overflow-hidden flex flex-col">
              <div className="h-44 bg-gray-100 flex items-center justify-center overflow-hidden">
                {s.url ? (
                  <img src={s.url} alt={s.label} className="w-full h-full object-cover" />
                ) : (
                  <div className="text-center text-gray-400">
                    <FiImage size={30} className="mx-auto mb-1" />
                    <div className="text-xs">Visuel d'origine</div>
                  </div>
                )}
              </div>

              <div className="p-4 flex-1 flex flex-col">
                <div className="font-semibold text-gray-900">{s.label}</div>
                <div className="text-xs text-gray-500 mb-3">
                  {s.url ? 'Photo personnalisée' : 'Aucune photo envoyée'}
                </div>

                <div className="mt-auto flex gap-2">
                  <button
                    onClick={() => pick(s.key)}
                    disabled={busy === s.key}
                    className="flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-primary-600 text-white text-sm font-semibold hover:bg-primary-700 disabled:opacity-60"
                  >
                    <FiUpload size={14} /> {busy === s.key ? 'Envoi...' : 'Changer'}
                  </button>
                  {s.url && (
                    <button
                      onClick={() => send(s.key, null, true)}
                      disabled={busy === s.key}
                      className="px-3 py-2 rounded-lg border text-red-600 hover:bg-red-50 disabled:opacity-60"
                      title="Revenir au visuel d'origine"
                    >
                      <FiTrash2 size={15} />
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <p className="text-xs text-gray-500 mt-6">
        Format conseillé : paysage, 1200 px de large minimum, {MAX_MO} Mo maximum.
      </p>
    </div>
  );
};

export default AdminHubImages;
